import React, { useState } from 'react';
import { Database, X, Download, Shield, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../api';

const BackupModal = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('total'); // 'total' or 'partial'
  const [days, setDays] = useState(7);
  const [status, setStatus] = useState(null); // { success: bool, message: string }

  const handleGenerateBackup = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const response = await api.post('backups/generate/', {
        days: type === 'partial' ? days : null
      });
      setStatus({ success: true, message: response.data.status });
    } catch (error) {
      console.error('Error generating backup:', error);
      setStatus({ 
        success: false, 
        message: error.response?.data?.error || 'Error al conectar con el servidor de backups.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content glassmorphism border-0 shadow-lg">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title section-title fs-4 mb-0 d-flex align-items-center">
              <Database size={24} className="me-2 text-warning" /> Generar Backup
            </h5>
            <button type="button" className="btn-close-premium" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          
          <div className="modal-body py-4">
            {!status ? (
              <>
                <p className="text-muted small mb-4">
                  El backup se enviará por correo electrónico a la lista de administradores configurada.
                  Contiene información de alumnos, notas, asistencias y registros de pagos.
                </p>

                <div className="d-flex flex-column gap-3">
                  {/* Opción Total */}
                  <div 
                    className={`card p-3 cursor-pointer border-2 transition-all ${type === 'total' ? 'border-primary bg-primary bg-opacity-10' : 'border-light hover-bg-light'}`}
                    onClick={() => setType('total')}
                  >
                    <div className="d-flex align-items-center">
                      <Shield className={`me-3 ${type === 'total' ? 'text-primary' : 'text-muted'}`} size={24} />
                      <div>
                        <h6 className="mb-0 fw-bold">Backup Total</h6>
                        <small className="text-muted">Exportar toda la base de datos histórica.</small>
                      </div>
                      <div className="ms-auto">
                        <div className={`rounded-circle border d-flex align-items-center justify-content-center ${type === 'total' ? 'bg-primary border-primary' : 'border-secondary'}`} style={{ width: '20px', height: '20px' }}>
                          {type === 'total' && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Opción Parcial */}
                  <div 
                    className={`card p-3 cursor-pointer border-2 transition-all ${type === 'partial' ? 'border-primary bg-primary bg-opacity-10' : 'border-light hover-bg-light'}`}
                    onClick={() => setType('partial')}
                  >
                    <div className="d-flex align-items-center">
                      <Clock className={`me-3 ${type === 'partial' ? 'text-primary' : 'text-muted'}`} size={24} />
                      <div className="flex-grow-1">
                        <h6 className="mb-0 fw-bold">Backup Parcial</h6>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <small className="text-muted">Últimos</small>
                          <input 
                            type="number" 
                            className="form-control form-control-sm text-center py-0" 
                            style={{ width: '60px' }}
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            min="1"
                          />
                          <small className="text-muted">días</small>
                        </div>
                      </div>
                      <div className="ms-auto">
                        <div className={`rounded-circle border d-flex align-items-center justify-content-center ${type === 'partial' ? 'bg-primary border-primary' : 'border-secondary'}`} style={{ width: '20px', height: '20px' }}>
                          {type === 'partial' && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className={`text-center py-4 px-3 rounded-3 ${status.success ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
                {status.success ? (
                  <CheckCircle2 size={48} className="text-success mb-3" />
                ) : (
                  <AlertCircle size={48} className="text-danger mb-3" />
                )}
                <h5 className={`fw-bold ${status.success ? 'text-success' : 'text-danger'}`}>
                  {status.success ? '¡Envío Exitoso!' : 'Error en el Backup'}
                </h5>
                <p className="text-dark mb-0">{status.message}</p>
              </div>
            )}
          </div>

          <div className="modal-footer border-0">
            {!status ? (
              <>
                <button type="button" className="btn btn-light rounded-pill px-4" onClick={onClose} disabled={loading}>
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                  onClick={handleGenerateBackup}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <Download size={18} />
                  )}
                  Iniciar Proceso
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-primary rounded-pill w-100" onClick={onClose}>
                Entendido
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupModal;
